import type { BoardCell, Street, CellType } from '../types';

// 40格棋盘配置（按PRD V1.1：移除股票交易所格）
// 棋盘按环形排列，从起点(0)开始顺时针

export const BOARD_SIZE = 40;

export const STREETS: Street[] = [
  { name: '像素小镇', color: '#4ECDC4', subColor: '#F7FFF7', propertyIds: [1, 2, 4, 5, 7, 38, 39] },
  { name: '蒸汽都市', color: '#FF6B6B', subColor: '#FFE66D', propertyIds: [9, 10, 12, 13, 15, 17] },
  { name: '霓虹城区', color: '#6C5CE7', subColor: '#A29BFE', propertyIds: [19, 20, 22, 23, 25, 26] },
  { name: '云端天际', color: '#FDCB6E', subColor: '#E17055', propertyIds: [29, 30, 32, 34, 35] },
];

// 计算租金：基础租金 * (1 + buildingLevel)
function calcRent(base: number, level: number): number {
  const multipliers = [1, 2.5, 5, 10];
  return Math.round(base * multipliers[level]);
}

const P: CellType = 'property';
const C: CellType = 'chance';
const E: CellType = 'event';
const T: CellType = 'tax';
const S: CellType = 'start';
const J: CellType = 'jail';
const A: CellType = 'auction';
const R: CellType = 'reverseShop';
const CA: CellType = 'casino';

// 地产租金表 [空地, 房子, 别墅, 酒店]
function rents(base: number): [number, number, number, number] {
  return [
    Math.round(base * 1),
    Math.round(base * 2.5),
    Math.round(base * 5),
    Math.round(base * 10),
  ];
}

export const BOARD_CELLS: BoardCell[] = [
  // 0: 起点 (右下角)
  { id: 0, type: S, name: '起点', emoji: '🚩', description: '经过或停留获得200金币' },

  // === 像素小镇 (底部右侧) ===
  { id: 1, type: P, name: '像素小屋', street: '像素小镇', price: 60,  rentTable: rents(6),  upgradeCost: 60,  emoji: '🏠' },
  { id: 2, type: P, name: '像素公寓', street: '像素小镇', price: 80,  rentTable: rents(8),  upgradeCost: 80,  emoji: '🏢' },
  { id: 3, type: C, name: '命运',     emoji: '❓', description: '抽取命运卡' },
  { id: 4, type: P, name: '像素商铺', street: '像素小镇', price: 100, rentTable: rents(12), upgradeCost: 100, emoji: '🏪' },
  { id: 5, type: P, name: '像素花园', street: '像素小镇', price: 140, rentTable: rents(16), upgradeCost: 140, emoji: '🌳' },
  { id: 6, type: R, name: '反转商店', emoji: '🔄', description: '购买反转卡（300-600金币）' },
  { id: 7, type: P, name: '像素广场', street: '像素小镇', price: 200, rentTable: rents(22), upgradeCost: 200, emoji: '🏛️' },

  // === 转角 → 左侧 ===
  { id: 8, type: E, name: '事件',     emoji: '⚡', description: '触发全局事件' },
  { id: 9, type: P, name: '蒸汽工厂', street: '蒸汽都市', price: 220, rentTable: rents(24), upgradeCost: 220, emoji: '🏭' },
  { id: 10, type: P, name: '齿轮车间', street: '蒸汽都市', price: 240, rentTable: rents(26), upgradeCost: 240, emoji: '⚙️' },
  { id: 11, type: T, name: '税收',     emoji: '💰', description: '缴纳200金币或总资产10%（取低值）' },
  { id: 12, type: P, name: '烟囱巷',   street: '蒸汽都市', price: 260, rentTable: rents(28), upgradeCost: 260, emoji: '💨' },
  { id: 13, type: P, name: '锅炉大道', street: '蒸汽都市', price: 280, rentTable: rents(30), upgradeCost: 280, emoji: '🔥' },
  { id: 14, type: A, name: '拍卖行',   emoji: '🔨', description: '暗箱拍卖' },

  // === 转角 → 顶部 ===
  { id: 15, type: P, name: '蒸汽宫殿', street: '蒸汽都市', price: 320, rentTable: rents(35), upgradeCost: 320, emoji: '🏰' },
  { id: 16, type: R, name: '反转商店', emoji: '🔄', description: '购买反转卡（300-600金币）' },
  { id: 17, type: P, name: '铁轨终点', street: '蒸汽都市', price: 400, rentTable: rents(44), upgradeCost: 400, emoji: '🚂' },
  { id: 18, type: C, name: '命运',     emoji: '❓', description: '抽取命运卡' },
  { id: 19, type: P, name: '霓虹街区', street: '霓虹城区', price: 440, rentTable: rents(48), upgradeCost: 440, emoji: '🌆' },
  { id: 20, type: P, name: '数据港',   street: '霓虹城区', price: 480, rentTable: rents(52), upgradeCost: 480, emoji: '💻' },

  // === 转角 → 右侧 ===
  { id: 21, type: J, name: '监狱',     emoji: '🔒', description: '暂停1回合或支付200保释金' },
  { id: 22, type: P, name: '芯片广场', street: '霓虹城区', price: 520, rentTable: rents(57), upgradeCost: 520, emoji: '🖥️' },
  { id: 23, type: P, name: 'AI大道',   street: '霓虹城区', price: 560, rentTable: rents(61), upgradeCost: 560, emoji: '🤖' },
  { id: 24, type: E, name: '事件',     emoji: '⚡', description: '触发全局事件' },
  { id: 25, type: P, name: '量子中心', street: '霓虹城区', price: 620, rentTable: rents(68), upgradeCost: 620, emoji: '🔮' },
  { id: 26, type: P, name: '全息广场', street: '霓虹城区', price: 700, rentTable: rents(77), upgradeCost: 700, emoji: '📡' },
  { id: 27, type: C, name: '命运',     emoji: '❓', description: '抽取命运卡' },

  // === 转角 → 底部 ===
  { id: 28, type: A, name: '拍卖行',   emoji: '🔨', description: '暗箱拍卖' },
  { id: 29, type: P, name: '云端别墅', street: '云端天际', price: 750, rentTable: rents(82), upgradeCost: 750, emoji: '🏡' },
  { id: 30, type: P, name: '天空花园', street: '云端天际', price: 800, rentTable: rents(88), upgradeCost: 800, emoji: '☁️' },
  { id: 31, type: T, name: '税收',     emoji: '💰', description: '缴纳200金币或总资产10%（取低值）' },
  { id: 32, type: P, name: '天顶塔',   street: '云端天际', price: 850, rentTable: rents(93), upgradeCost: 850, emoji: '🗼' },
  { id: 33, type: CA, name: '赌场',     emoji: '🎰', description: 'V1.3 迷你游戏' },
  { id: 34, type: P, name: '苍穹广场', street: '云端天际', price: 920, rentTable: rents(101), upgradeCost: 920, emoji: '🌌' },
  { id: 35, type: P, name: '星辰大厦', street: '云端天际', price: 1000, rentTable: rents(110), upgradeCost: 1000, emoji: '⭐' },
  { id: 36, type: R, name: '反转商店', emoji: '🔄', description: '购买反转卡（300-600金币）' },
  { id: 37, type: C, name: '命运',     emoji: '❓', description: '抽取命运卡' },
  { id: 38, type: P, name: '像素杂货', street: '像素小镇', price: 160, rentTable: rents(18), upgradeCost: 160, emoji: '🛒' },
  { id: 39, type: P, name: '像素乐园', street: '像素小镇', price: 180, rentTable: rents(20), upgradeCost: 180, emoji: '🎠' },
];

// 获取格子所在的屏幕坐标（用于环形棋盘渲染）
export function getCellPosition(index: number, boardSize: number = 2400, cols: number = 11): { x: number; y: number; rotation: number } {
  // 左上角(0,0)到右下角为棋盘范围
  // 40格环形排列：每边约10格+转角
  const cellSize = 180; // px
  const padding = 60;
  const halfSize = boardSize / 2;
  const radius = halfSize - padding;

  // 简化算法：按顺序分布在矩形边上
  // 底边 (0-10): 11格
  // 左边 (11-20): 10格
  // 顶边 (21-30): 10格
  // 右边 (31-39): 9格
  let x: number, y: number, rotation: number;

  if (index <= 10) {
    // 底边：从右到左
    x = halfSize - (index * cellSize);
    y = halfSize + radius - cellSize / 2;
    rotation = 0;
  } else if (index <= 20) {
    // 左边：从下到上
    x = halfSize - radius + cellSize / 2;
    y = halfSize - ((index - 10) * cellSize);
    rotation = 90;
  } else if (index <= 30) {
    // 顶边：从左到右
    const n = index - 20;
    x = halfSize - radius + cellSize + ((n - 1) * cellSize);
    y = halfSize - radius + cellSize / 2;
    rotation = 180;
  } else {
    // 右边：从上到下
    const n = index - 30;
    x = halfSize + radius - cellSize / 2;
    y = halfSize - radius + cellSize + ((n - 1) * cellSize);
    rotation = 270;
  }

  return { x, y, rotation };
}
